import { z } from 'zod';
import { IMPORT_LIMITS } from '../constants/app';
import {
  ARTICLE_VISIBILITIES,
  COPYRIGHT_STATUSES,
  IMPORT_SOURCES,
  SEGMENT_STATUSES,
} from '../types/index';
import { copyrightAckSchema, pageQuerySchema } from './common';

/**
 * 内容（教材 / 单元 / 文章）相关的请求校验。
 *
 * 三条贯穿全文件的约定：
 * 1. **所有 message 都是中文**：校验失败的文案会原样出现在接口返回里，
 *    前端不需要再翻译一遍（见 apps/api 的 `parseJsonBody`）。
 * 2. **凡是来自 query 的布尔值都显式解析**：`z.coerce.boolean()` 会把字符串 `"false"`
 *    变成 `true`，用它做筛选条件必然出错，因此统一走下面的 `booleanQuery`。
 * 3. **长度上限来自 `IMPORT_LIMITS`**：正文长度同时决定接口校验、R2 落盘阈值与错误码 413，
 *    三处必须同一个数字。
 */

/** query 里的布尔值：只接受 "true"/"false"/"1"/"0"，其余按未提供处理。 */
export const booleanQuery = z
  .union([z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0'), z.boolean()])
  .transform((value) => value === true || value === 'true' || value === '1')
  .optional();

// ---------------------------------------------------------------------------
// 教材
// ---------------------------------------------------------------------------

export const textbookCreateSchema = z.object({
  title: z.string().trim().min(1, '请输入教材名称').max(200, '教材名称过长'),
  titleZh: z.string().trim().max(200, '中文名称过长').optional(),
  publisher: z.string().trim().max(120, '出版社名称过长').optional(),
  edition: z.string().trim().max(60, '版次过长').optional(),
  /** 平台当前只服务「大学英语三」，默认值即筛选条件 */
  level: z.string().trim().min(1).max(40).default('大学英语三'),
  description: z.string().trim().max(2000, '简介过长').optional(),
  /** 留空则由服务端按 title 生成；生成规则见 textbook.service 的 slugify */
  slug: z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'slug 只能包含小写字母、数字与连字符')
    .optional(),
  copyrightStatus: z.enum(COPYRIGHT_STATUSES).default('public_metadata'),
  visibility: z.enum(ARTICLE_VISIBILITIES).default('public'),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});
export type TextbookCreateInput = z.infer<typeof textbookCreateSchema>;

export const textbookUpdateSchema = textbookCreateSchema.partial();
export type TextbookUpdateInput = z.infer<typeof textbookUpdateSchema>;

export const textbookListQuerySchema = z.object({
  level: z.string().trim().max(40).optional(),
  /** 默认带上单元与文章，一次请求渲染完目录页 */
  withUnits: booleanQuery,
});
export type TextbookListQuery = z.infer<typeof textbookListQuerySchema>;

// ---------------------------------------------------------------------------
// 单元
// ---------------------------------------------------------------------------

export const unitCreateSchema = z.object({
  textbookId: z.string().trim().min(1, '请选择所属教材').max(64),
  unitNo: z.coerce.number().int().min(1, '单元号从 1 开始').max(999),
  title: z.string().trim().min(1, '请输入单元标题').max(200, '单元标题过长'),
  titleZh: z.string().trim().max(200, '中文标题过长').optional(),
  theme: z.string().trim().max(120, '主题过长').optional(),
  summary: z.string().trim().max(2000, '单元说明过长').optional(),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});
export type UnitCreateInput = z.infer<typeof unitCreateSchema>;

/**
 * 单元修改。刻意**不含 textbookId**：把单元从一个教材挪到另一个教材，
 * 会让该单元下所有文章与教材不一致，正确做法是新建单元 + 重新归属文章。
 */
export const unitUpdateSchema = z.object({
  unitNo: z.coerce.number().int().min(1, '单元号从 1 开始').max(999).optional(),
  title: z.string().trim().min(1, '请输入单元标题').max(200, '单元标题过长').optional(),
  titleZh: z.string().trim().max(200, '中文标题过长').optional(),
  theme: z.string().trim().max(120, '主题过长').optional(),
  summary: z.string().trim().max(2000, '单元说明过长').optional(),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional(),
});
export type UnitUpdateInput = z.infer<typeof unitUpdateSchema>;

// ---------------------------------------------------------------------------
// 文章导入
// ---------------------------------------------------------------------------

/**
 * 导入文章。
 *
 * ⚠️ `acknowledgeCopyright` 用 `partial()` 放宽成**可选**，这不是放松合规要求，
 * 而是为了把错误码做对：如果让 zod 去校验"必须为 true"，缺失时会变成
 * 400 `VALIDATION_FAILED`（"输入不合法"），用户看到的是"我格式填错了"。
 * 实际上这是**授权确认缺失**，应该返回 422 `COPYRIGHT_ACK_REQUIRED`
 * 并给出"请先确认你拥有该文本的使用授权"。所以真正的闸门在路由里：
 * 拿到 body 后显式判断 `acknowledgeCopyright !== true` 才放行。
 */
export const articleImportSchema = copyrightAckSchema.partial().extend({
  title: z.string().trim().min(1, '请输入文章标题').max(IMPORT_LIMITS.maxTitleLength, '标题过长'),
  titleZh: z.string().trim().max(IMPORT_LIMITS.maxTitleLength, '中文标题过长').optional(),
  /**
   * 正文。超过 `IMPORT_LIMITS.r2ThresholdChars` 时服务端自动转存 R2。
   *
   * 上限刻意设成**产品上限的 3 倍**，让两件事各归其位：
   * - `< 3×` 的越界正文会通过 zod，由服务层判定并返回 **413 PAYLOAD_TOO_LARGE**
   *   （"内容过长，请拆分后分批导入"）—— 这是用户真正会遇到的错误；
   * - 只有大到离谱的 body（3× 以上）才在这里被拦成 400，作用是**避免为了报错先把
   *   几百 KB 的正文解析一遍**。
   * 如果这里直接用产品上限，所有越界都变成 400，413 这个错误码就永远不会出现，
   * "太长"与"格式不对"也就没法区分了。
   */
  content: z
    .string()
    .min(1, '请输入文章正文')
    .max(
      IMPORT_LIMITS.maxArticleChars * 3,
      '正文大到不像是文章，请确认是否粘贴了无关内容',
    ),
  textbookId: z.string().trim().max(64).optional(),
  unitId: z.string().trim().max(64).optional(),
  author: z.string().trim().max(120, '作者名过长').optional(),
  source: z.string().trim().max(200, '来源说明过长').optional(),
  sourceUrl: z.string().trim().max(500, '链接过长').optional(),
  /** 授权说明：谁授权的、依据是什么。合规要求留痕 */
  licenseNote: z.string().trim().max(500, '授权说明过长').optional(),
  importSource: z.enum(IMPORT_SOURCES).default('paste'),
  /** 默认私有：用户导入的文本先归自己，确认无误再公开 */
  visibility: z.enum(ARTICLE_VISIBILITIES).default('private'),
  levelTag: z.string().trim().max(40).optional(),
});
/**
 * 服务层入参：去掉路由层的合规开关。
 *
 * `acknowledgeCopyright` 只对"是否放行这次导入"有意义，业务逻辑拿到的是
 * 已经被授权过的请求，因此不必也不该再看到这个字段。
 */
export type ArticleImportInput = Omit<z.infer<typeof articleImportSchema>, 'acknowledgeCopyright'>;

/** 导入后允许修改的元数据（不含正文，正文修改走重新导入）。 */
export const articleUpdateSchema = z.object({
  title: z.string().trim().min(1).max(IMPORT_LIMITS.maxTitleLength).optional(),
  titleZh: z.string().trim().max(IMPORT_LIMITS.maxTitleLength).optional(),
  author: z.string().trim().max(120).optional(),
  source: z.string().trim().max(200).optional(),
  textbookId: z.string().trim().max(64).nullable().optional(),
  unitId: z.string().trim().max(64).nullable().optional(),
  levelTag: z.string().trim().max(40).optional(),
  visibility: z.enum(ARTICLE_VISIBILITIES).optional(),
  licenseNote: z.string().trim().max(500).optional(),
});
export type ArticleUpdateInput = z.infer<typeof articleUpdateSchema>;

export const articleListQuerySchema = pageQuerySchema.extend({
  textbookId: z.string().trim().max(64).optional(),
  unitId: z.string().trim().max(64).optional(),
  level: z.string().trim().max(40).optional(),
  /** 标题模糊搜索 */
  q: z.string().trim().max(100).optional(),
  segmentStatus: z.enum(SEGMENT_STATUSES).optional(),
  /** 只看含长难句的文章 */
  onlyLong: booleanQuery,
  /** me：只列自己导入的（私人库）；all：公共库 */
  scope: z.enum(['me', 'public', 'all']).default('all'),
});
export type ArticleListQuery = z.infer<typeof articleListQuerySchema>;

// ---------------------------------------------------------------------------
// 阅读进度
// ---------------------------------------------------------------------------

/**
 * 阅读上报。设计成"增量"而不是"全量覆盖"：
 * 持续滚动会高频上报，如果是全量覆盖，一次乱序到达的旧请求就会把进度打回去。
 */
export const progressUpdateSchema = z.object({
  /** 当前读到的句子序号（全局句序） */
  lastGlobalIndex: z.coerce.number().int().min(0).max(100_000).optional(),
  /** 本次新增的阅读秒数（服务端累加，单次上限 1 小时，防止挂机刷时长） */
  readSecondsDelta: z.coerce.number().int().min(0).max(3600).optional(),
  /** 是否标记为已读完 */
  finished: z.boolean().optional(),
  /** 1–5 的自评（读完后的"这篇难不难"） */
  selfRating: z.number().int().min(1).max(5).optional(),
});
export type ProgressUpdateInput = z.infer<typeof progressUpdateSchema>;
